import React, { useEffect, useState } from "react";
import { ContentMainLayout } from "../../layout/contentLayout";
import { Box, Button, Stack, Typography } from "@mui/material";
import useResponsive from "../../hooks/useResponsive";

import BadgeImage from "../ui-elements/badge-image";
import ImageGrid from "../image-grid";
import SignPetitionContainer from "../sign-petition-container";
import { PostContentDetails } from "../../interface/posts";
import TypographyContent from "../ui-elements/typography-content";
import {
  formatDateAndTimeFromString,
  formatTextType,
} from "../../utils/date-setup";
import { CommentReplyPropsData } from "../../interface/comment";
import TurnedInIcon from "@mui/icons-material/TurnedIn";
import TurnedInNotIcon from "@mui/icons-material/TurnedInNot";

import { apiBookmarkPost } from "../../api/post";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  FlagContainedIcon,
  FlagOutlineIcon,
  HandsFistIcon,
} from "../ui-elements/svg-icons";
import { useDrawer } from "../../hooks/useDrawer";
import { FlagModalContents } from "../ui-elements/flag-post";
import PetitionSignatures from "../petition-signatures";
import { useCommentAndReply } from "../../hooks/usecomment";
import {
  apiAddComment,
  apiAddReply,
  apiGetCommentReply,
  apiLikeCommentReply,
} from "../../utils/comment-utils";
import CommentContainer from "../ui-elements/comment-container";
import Comment from "../ui-elements/comment-content";

const PetitionContent = ({
  post_details,
  comment_data,
  refetch_comment,
}: {
  post_details: PostContentDetails;
  comment_data: any[];
  refetch_comment: () => void;
}) => {
  const isDesktop = useResponsive("down", "lg", "xl");
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");
  const postId = post_details?.id;
  const initialComments = comment_data;
  const account_profile_data = useSelector(
    (state: RootState) => state.content.account_profile_data
  );

  const { openDrawer } = useDrawer();
  const {
    comments,
    newCommentContent,
    setNewCommentContent,
    handleAddComment,
    handleAddReply,
    handleLike,
    handleLoadReplies,
    loadingStates,
    expandedComments,
  } = useCommentAndReply({
    postId,
    initialComments,
    onAddComment: async (content, postId) => {
      return apiAddComment(content, postId, account_profile_data);
    },
    onAddReply: async (parentId, content, postId) => {
      return apiAddReply(parentId, content, postId, account_profile_data);
    },
    onLike: async (commentId) => {
      // Mock or actual API call here
      return apiLikeCommentReply(commentId);
    },
    onLoadReplies: async (commentId) => {
      // Mock or actual API call here
      return apiGetCommentReply(commentId, postId);
    },
  });

  return (
    <ContentMainLayout>
      <Stack
        direction={"row"}
        spacing={4}
        justifyContent={isDesktop ? "center" : "space-between"}
      >
        <Stack width={isSmall ? "100%" : 560}>
          <Box
            id="main-report-scroll-container"
            sx={{
              width: isMedium ? "100%" : "95%",
              mx: isMedium ? 0 : 2,
              mt: "auto",
              height: "76vh",
              overflowY: "auto",
              pt: "85px",
              px: isMedium ? 0 : 3,
              position: "relative",
            }}
          >
            {isDesktop && (
              <Box
                sx={{ position: "fixed", right: 10, width: 80, height: "3rem" }}
              >
                <Button
                  variant="contained"
                  onClick={() =>
                    openDrawer(
                      account_profile_data?.id === post_details.author_id ? (
                        <PetitionSignatures
                          author_id={post_details?.author_id}
                          petition_id={post_details?.id}
                        />
                      ) : (
                        <SignPetitionContainer data={post_details} />
                      )
                    )
                  }
                >
                  <HandsFistIcon />
                </Button>
              </Box>
            )}
            <PetitionContentDetails
              post_details={post_details}
              comment_list={comments}
              handleLoadReplies={handleLoadReplies}
              handleAddReply={handleAddReply}
              handleLike={handleLike}
              expandedComments={expandedComments}
              loadingStates={loadingStates}
            />
            <Box
              sx={{
                position: "fixed",
                width: isMedium ? "80%" : 600,
                mx: "auto",
                bottom: 10,
                p: 1,
                py: 2,
                backgroundColor: "#FAFAFA",
              }}
            >
              <CommentContainer
                newCommentContent={newCommentContent}
                setNewCommentContent={setNewCommentContent}
                handleAddComment={handleAddComment}
                data={post_details}
              />
            </Box>
          </Box>
        </Stack>
        <Stack
          width={isDesktop ? "0" : "35%"}
          sx={{
            display: isDesktop ? "none" : "block",
          }}
          height={505}
        >
          <Box
            sx={{
              width: "100%",
              pt: "85px",
              height: "76vh",
            }}
          >
            {account_profile_data?.id === post_details.author_id ? (
              <PetitionSignatures
                author_id={post_details?.author_id}
                petition_id={post_details?.id}
              />
            ) : (
              <SignPetitionContainer data={post_details} />
            )}
          </Box>
        </Stack>
      </Stack>
    </ContentMainLayout>
  );
};

export default PetitionContent;

export const PetitionContentDetails = ({
  post_details,
  comment_list,
  handleLoadReplies,
  handleAddReply,
  handleLike,
  expandedComments,
  loadingStates,
}: {
  post_details: PostContentDetails;
  comment_list: any[];
  handleLoadReplies: (commentId: number) => Promise<void>;
  handleAddReply: (parentId: number, content: string) => Promise<void>;
  loadingStates: {
    [key: number]: boolean;
  };
  expandedComments: Set<number>;
  handleLike: (commentId: number) => Promise<void>;
}) => {
  const [bookmarked, setBookmarked] = useState(
    post_details?.current_user_bookmarked
  );
  const [flag_modal, setFlag_modal] = useState(false);
  const [flag, setFlag] = useState(false);
  const toggle_modal = () => setFlag_modal(!flag_modal);
  const toggle_flag = () => setFlag(!flag);

  // handle bookmark
  const bookmark_post = async (post_id: number) => {
    try {
      const response = await apiBookmarkPost(post_id);
      if (response.status === 200) {
        //
      } else {
      }
    } catch (error) {}
  };

  const handleBookmark = (id: number) => {
    if (bookmarked) {
      setBookmarked(false);
      bookmark_post(id);
    } else {
      setBookmarked(true);
      bookmark_post(id);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <FlagModalContents
        open={flag_modal}
        toggle_flag={toggle_flag}
        handle_modal={toggle_modal}
        post_id={post_details?.id}
        reported={post_details?.reported}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 3,
        }}
      >
        <Stack direction="row" spacing={1} alignItems={"center"}>
          <img
            src={post_details?.author_photo_url}
            style={{ width: 50, height: 50, borderRadius: 40 }}
          />
          <Stack>
            <Stack direction={"row"} spacing={1}>
              <Typography variant="subtitle2" color="black">
                {post_details?.author}
              </Typography>
              <BadgeImage badge={post_details?.author_badge} />
            </Stack>

            <Typography
              variant="caption"
              sx={{
                color: "#4A5E6D",
                fontWeight: 400,
              }}
            >
              {formatDateAndTimeFromString(post_details?.created_at)}
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              sx={{
                fontWeight: 400,
                fontStyle: "italic",
              }}
            >
              {formatTextType(post_details?.post_type)}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction={"row"} spacing={1}>
          <Button onClick={toggle_modal}>
            {flag ? <FlagContainedIcon /> : <FlagOutlineIcon />}
          </Button>
          <Button onClick={() => handleBookmark(post_details?.id)}>
            {bookmarked ? <TurnedInIcon /> : <TurnedInNotIcon />}
          </Button>
        </Stack>
      </Box>
      <Box sx={{ ml: 4 }}>
        <Stack>
          <Typography
            color="black"
            sx={{
              fontSize: 18,
              marginBottom: 2,
              fontWeight: 500,
            }}
          >
            {post_details?.title}
          </Typography>
          <ImageGrid
            images={post_details?.media} // Pass images as an array
          />
        </Stack>
        <Box sx={{ mt: "10px", display: "block" }}>
          <TypographyContent content={post_details?.context} />
        </Box>
      </Box>
      {comment_list?.length > 0 && (
        <Typography
          variant="h6"
          color="primary"
          sx={{ fontWeight: 600, mt: 4 }}
        >
          Comments
        </Typography>
      )}
      {comment_list.map((comment) => (
        <Comment
          key={comment.id}
          comment={comment}
          onLoadReplies={handleLoadReplies}
          onAddReply={handleAddReply}
          onLike={handleLike}
          isExpanded={expandedComments.has(comment.id)}
          isLoading={loadingStates[comment.id] || false}
          level={0}
        />
      ))}
    </Box>
  );
};
